/**
 * MailSettingsContent — Profile Settings "Mail" tab.
 *
 * Everything here is found automatically by the backend: the signature (from
 * the user's sent mail, else their Microsoft profile), the company domains
 * that make a mail "internal", and their manager and direct reports. The
 * signature is read-only -- one signature, always the one from the mailbox.
 * Reading the mailbox and learning how the user writes are automatic; what's
 * learned is kept in Memory (Settings > Memory), where it can be edited or
 * deleted. The user can switch the signature off, sync everything again,
 * and change how mail to colleagues and to clients should sound.
 */
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FiMail, FiRefreshCw } from 'react-icons/fi';

import { ENABLE_MAIL_TONE } from '../../config/features';
import { DataState } from '../../context/DataProvider';
import * as mailApi from '../../services/mailApi';
import { sanitizeHtml } from '../../utils/sanitize';
import { signatureForDark } from '../../utils/signatureForDark';

const TONES = [
  { key: 'tone_internal', prop: 'toneInternal', label: 'To colleagues' },
  {
    key: 'tone_external',
    prop: 'toneExternal',
    label: 'To clients & outsiders',
  },
];

const errorMessage = (err, fallback) =>
  (typeof err === 'string' ? err : err?.message) || fallback;

// Inside the preview's own shadow root, so the app's styles (its font, its
// light font weight, its resets) do not reach the signature.
const SIGNATURE_BASE_STYLE = `
  :host { display: block; }
  /* Reset here, not on :host: the page's own rules win on the host element,
     and its light font weight would otherwise make <b> only "normal". */
  .signature {
    all: initial;
    display: block;
    font-family: Aptos, Calibri, Helvetica, Arial, sans-serif;
    font-size: 12pt;
    font-weight: 400;
    line-height: normal;
    color: rgb(212, 212, 216);
    overflow-wrap: anywhere;
  }
  a { color: #8b88e6; }
  img { max-width: 100%; }
  table { border-collapse: collapse; }
`;

/**
 * The user's signature in the app's dark style, with its own fonts, sizes,
 * bold, colours and pictures. Colours too dark to read on dark are lightened
 * (utils/signatureForDark.js). Preview only: the sent mail keeps the
 * signature exactly as it is stored.
 */
export function SignaturePreview({ html, className = '' }) {
  const hostRef = useRef(null);
  // Sanitized server-side when read from the mailbox, and again here.
  const shown = signatureForDark(sanitizeHtml(html));

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const root = host.shadowRoot || host.attachShadow({ mode: 'open' });
    root.innerHTML = `<style>${SIGNATURE_BASE_STYLE}</style><div class="signature">${shown}</div>`;
  }, [shown]);

  return (
    <div
      data-testid="signature-preview"
      className={`overflow-x-auto rounded-md border border-zinc-700/60 bg-zinc-800/50 px-3 py-2 ${className}`}
    >
      <div ref={hostRef} />
    </div>
  );
}

const Card = ({ title, subtitle, actions, children }) => (
  <div className="rounded-md border border-zinc-800 bg-zinc-900/60 p-3">
    <div className="mb-2 flex items-center justify-between gap-2">
      <div>
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
          {title}
        </h4>
        {subtitle && <p className="text-[10px] text-zinc-500">{subtitle}</p>}
      </div>
      {actions}
    </div>
    {children}
  </div>
);

const SmallButton = ({ primary, children, ...props }) => (
  <button
    {...props}
    className={`flex items-center gap-1.5 rounded px-2.5 py-1.5 text-[11px] disabled:opacity-50 ${
      primary
        ? 'bg-[#5652CF] font-medium text-white hover:bg-[#4642bf]'
        : 'border border-zinc-700 text-zinc-300 hover:bg-zinc-800'
    }`}
  >
    {children}
  </button>
);

export default function MailSettingsContent() {
  const { userGoogleId: accessToken } = DataState();
  const [profile, setProfile] = useState(null);
  const [defaultTones, setDefaultTones] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toneDrafts, setToneDrafts] = useState({});

  const applyResponse = (res) => {
    setProfile(res?.data ?? null);
    if (res?.meta?.default_tones) setDefaultTones(res.meta.default_tones);
    setToneDrafts({});
  };

  useEffect(() => {
    let cancelled = false;
    mailApi
      .getMailProfile(accessToken)
      .then((res) => !cancelled && applyResponse(res))
      .catch((err) =>
        toast.error(errorMessage(err, 'Could not load mail settings.')),
      )
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  const save = async (changes, successText) => {
    setIsSaving(true);
    try {
      applyResponse(await mailApi.updateMailProfile(accessToken, changes));
      if (successText) toast.success(successText);
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save mail settings.'));
    } finally {
      setIsSaving(false);
    }
  };

  // One sync reads everything from Outlook again: signature, company
  // domains, manager, direct reports -- and re-learns how the user writes.
  // It also happens on its own once the data is a week old.
  const sync = async () => {
    setIsSaving(true);
    try {
      applyResponse(await mailApi.syncWithOutlook(accessToken));
      toast.success('Synced with Outlook.');
    } catch (err) {
      toast.error(errorMessage(err, 'Could not sync with Outlook.'));
    } finally {
      setIsSaving(false);
    }
  };

  const lastSynced = profile?.detected_at
    ? new Date(profile.detected_at).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : null;

  const enabled = profile?.signature_enabled ?? true;
  const toneDirty = Object.keys(toneDrafts).length > 0;

  return (
    <div className="p-1">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-base font-bold text-zinc-100">
            <FiMail className="h-4 w-4 text-[#5652CF]" />
            Mail
          </h3>
          <p className="text-[10px] text-zinc-500">
            Your Outlook signature is added automatically to every mail sent
            from chat, and mail is written differently for colleagues and for
            clients. How you write is learned automatically and kept in Memory.
            You don&apos;t need to set anything up.
          </p>
        </div>
        {profile && (
          <div className="flex flex-shrink-0 items-center gap-2">
            {lastSynced && (
              <span className="text-[10px] text-zinc-500">
                Synced {lastSynced}
              </span>
            )}
            <button
              type="button"
              onClick={sync}
              disabled={isSaving}
              title="Sync with Outlook: signature, company domains, manager and team"
              className="flex items-center gap-1 rounded border border-zinc-700 px-1.5 py-0.5 text-[10px] text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
            >
              <FiRefreshCw
                className={`h-2.5 w-2.5 ${isSaving ? 'animate-spin' : ''}`}
              />
              Sync
            </button>
          </div>
        )}
      </div>

      {isLoading ? (
        <p className="text-[11px] text-zinc-500">Loading…</p>
      ) : !profile ? (
        <p className="rounded-md border border-dashed border-zinc-800 px-3 py-4 text-center text-[11px] text-zinc-500">
          Connect Outlook and your signature will be picked up automatically.
        </p>
      ) : (
        <div className="space-y-3">
          <label className="flex cursor-pointer items-center justify-between rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2.5">
            <span className="text-[12px] text-zinc-200">
              Add my signature to mail sent from chat
            </span>
            <input
              type="checkbox"
              className="h-4 w-4 accent-[#5652CF]"
              checked={enabled}
              disabled={isSaving}
              onChange={(e) =>
                save({ signatureEnabled: e.target.checked }, null)
              }
            />
          </label>

          <Card
            title="Signature"
            subtitle="Read from your Outlook. Change it in Outlook, then Sync."
          >
            {profile.signature_html ? (
              <SignaturePreview
                html={profile.signature_html}
                className={enabled ? '' : 'opacity-50'}
              />
            ) : (
              <p className="text-[11px] text-zinc-500">
                No signature found in your sent mail or profile.
              </p>
            )}
          </Card>

          {ENABLE_MAIL_TONE && (
            <>
              <Card
                title="How mail should sound"
                subtitle="The assistant picks one from the recipients. Leave blank for the default."
                actions={
                  toneDirty && (
                    <SmallButton
                      primary
                      disabled={isSaving}
                      onClick={() =>
                        save(
                          Object.fromEntries(
                            TONES.filter(({ key }) => key in toneDrafts).map(
                              ({ key, prop }) => [prop, toneDrafts[key]],
                            ),
                          ),
                          'Saved.',
                        )
                      }
                    >
                      Save
                    </SmallButton>
                  )
                }
              >
                <div className="space-y-2">
                  {TONES.map(({ key, label }) => (
                    <label key={key} className="block">
                      <span className="mb-1 block text-[11px] text-zinc-400">
                        {label}
                      </span>
                      <textarea
                        rows={2}
                        maxLength={2000}
                        className="w-full resize-y rounded-md border border-zinc-700/50 bg-zinc-900/40 p-2 text-[12px] text-zinc-200 placeholder-zinc-600 outline-none focus:border-[#5652CF]"
                        placeholder={defaultTones[key.replace('tone_', '')]}
                        value={toneDrafts[key] ?? profile[key] ?? ''}
                        onChange={(e) =>
                          setToneDrafts((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                      />
                    </label>
                  ))}
                </div>
              </Card>

              <Card
                title="Detected automatically"
                subtitle="From Outlook, refreshed with each sync. Used to tell internal mail from external mail."
              >
                <dl className="grid grid-cols-[110px_1fr] gap-y-1 text-[11px]">
                  <dt className="text-zinc-500">Company domains</dt>
                  <dd className="text-zinc-300">
                    {profile.org_domains?.join(', ') || 'None found'}
                  </dd>
                  <dt className="text-zinc-500">Manager</dt>
                  <dd className="text-zinc-300">
                    {profile.manager_email || 'None found'}
                  </dd>
                  <dt className="text-zinc-500">Direct reports</dt>
                  <dd className="text-zinc-300">
                    {profile.report_emails?.length
                      ? profile.report_emails.join(', ')
                      : 'None found'}
                  </dd>
                </dl>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
}
